import { get, merge, has } from 'lodash';
import { BaseClient as InfraBaseClient, sessionManager } from 'vue-infra';
import BaseClient from '../common/BaseClient';
import ApiResources from '../data/apiResources';
import { MEDALLIA_TIMEOUT_ERROR_CODE,
  SOURCE_APP_NAME,
  MEDALLIA_DOMAINS,
  MEDALLIA_SURVEY_ID_FIELD,
  MEDALLIA_PLATFORM_DOMAIN } from '../common/consts';

const RESOURCES = {
  MEDALLIA_INITIALIZATION_INFO: 'medalliaIntializationInfo',
  MEDALLIA_ACCOUNT_INFO: 'medalliaAccountInfo',
  MEDALLIA_ALL_SURVEYS: 'medalliaAllSurveys',
  MEDALLIA_ALL_SKILLS: 'medalliaAllSkills',
  MEDALLIA_TEST_API_CONFIG: 'medalliaTestAPIConfig',
  MEDALLIA_MAPPING_CONFIG: 'medalliaMappingConfig',
  MEDALLIA_SAVE_CONFIG_TO_DB: 'medalliaSaveConfigToDB',
  MEDALLIA_SURVEY_SKILLS_TO_DB: 'medalliaSurveySkillsToDB',
  MEDALLIA_DEL_SURVEY_SKILL_FROM_DB: 'medalliaDelSurveySkillFromDB',
  MEDALLIA_SKILL_INTERACTIVENESS: 'medalliaUpdateSkillInteractiveness',
};

const RESOURCES_CONFIGURATION = {
  [RESOURCES.MEDALLIA_TEST_API_CONFIG]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.POST],
    URL_KEY: () => 'MEDALLIA.TestAPIConfig',
  },
  [RESOURCES.MEDALLIA_SAVE_CONFIG_TO_DB]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.POST],
    URL_KEY: () => 'MEDALLIA.SaveConfigToDB',
  },
  [RESOURCES.MEDALLIA_PATCH_CONFIG_TO_DB]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.PATCH],
    URL_KEY: () => 'MEDALLIA.AccountInfo',
  },
  [RESOURCES.MEDALLIA_MAPPING_CONFIG]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.POST],
    URL_KEY: () => 'MEDALLIA.MappingConfig',
  },
  [RESOURCES.MEDALLIA_SURVEY_SKILLS_TO_DB]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.POST],
    URL_KEY: () => 'MEDALLIA.MappingConfig',
  },
  [RESOURCES.MEDALLIA_DEL_SURVEY_SKILL_FROM_DB]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.DELETE],
    URL_KEY: () => 'MEDALLIA.MappingConfig',
  },
  [RESOURCES.MEDALLIA_INITIALIZATION_INFO]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.POST],
    URL_KEY: () => 'MEDALLIA.Initialization',
  },
  [RESOURCES.MEDALLIA_ACCOUNT_INFO]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'MEDALLIA.AccountInfo',
  },
  [RESOURCES.MEDALLIA_ALL_SURVEYS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'MEDALLIA.AllSurveys',
  },
  [RESOURCES.MEDALLIA_ALL_SKILLS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'MEDALLIA.AllSkills',
  },
  [RESOURCES.MEDALLIA_SKILL_INTERACTIVENESS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.PUT, BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'MEDALLIA.Interactiveness',
  },
};

class MedalliaClient extends BaseClient {
  constructor({
    resource,
    contextName,
    ...options
  }) {
    const infraBaseClient = createMedalliaBaseClient(options, resource, contextName);
    super({
      name: 'MedalliaClient',
      client: infraBaseClient,
      ...options,
    });
  }
}

function createMedalliaBaseClient(options, resource, contextName) {
  // TODO: need to implement out own urlResolver
  const urlResolver = window.LE.urlResolver;
  setIhubDomains(urlResolver, options);
  const objFromApiResources = get(ApiResources, RESOURCES_CONFIGURATION[resource].URL_KEY());
  const baseURL = urlResolver.getUrl(objFromApiResources);
  const params = objFromApiResources ? objFromApiResources.params : {};
  const medalliaBaseClientOptions = {
    allowedMethods: RESOURCES_CONFIGURATION[resource].ALLOWED_METHODS,
    source: {
      appName: SOURCE_APP_NAME,
      contextName,
    },
    config: {
      baseURL,
      timeout: RESOURCES_CONFIGURATION[resource].TIMEOUT,
      params,
    },
    retryConfig: {
      retries: 1,
      retryCondition: error => get(error, 'response.status') !== MEDALLIA_TIMEOUT_ERROR_CODE,
      retryDelay: () => 1000,
      shouldResetTimeout: true,
    },
  };
  merge(medalliaBaseClientOptions, options);
  return new InfraBaseClient(medalliaBaseClientOptions);
}

// Todo: Remove when medalliaPlatform and ihubshellPlatform domains will be added to CSDS
function setIhubDomains(urlResolver, options) {
  let accountZone = sessionManager.getAccountZone();
  if (accountZone === 'qa' || !has(MEDALLIA_DOMAINS, accountZone)) {
    accountZone = 'z0';
  }
  const medalliaPlatformDomain = MEDALLIA_DOMAINS[accountZone].medalliaPlatform;
  const surveyId = options.surveyId;
  urlResolver.addConfigValue(MEDALLIA_PLATFORM_DOMAIN, medalliaPlatformDomain);
  urlResolver.addConfigValue(MEDALLIA_SURVEY_ID_FIELD, surveyId);
}

MedalliaClient.RESOURCES = RESOURCES;
MedalliaClient.RESOURCES_CONFIGURATION = RESOURCES_CONFIGURATION;

export default MedalliaClient;
